/**
 * Enhanced Suggestion Card Component
 * 
 * This is an example implementation showing how to enhance AI suggestions
 * with visuals, badges, and improved information display.
 * 
 * Features demonstrated:
 * - Destination/activity images
 * - Activity type badges
 * - Budget breakdown visualization
 * - Weather information
 * - Expandable details
 * - Confidence/priority indicators
 */

import { useState } from 'react';
import { View, Pressable, Image, ActivityIndicator, Linking, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { VoteButton } from '@/components/collaboration/VoteButton';
import { ThemedText, SubHeading, Body, Caption } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import { useLandmarkImage } from '@/hooks/useLandmarkImage';

export type ActivityType = 
  | 'food' 
  | 'culture' 
  | 'adventure' 
  | 'nature' 
  | 'nightlife' 
  | 'shopping' 
  | 'relaxation' 
  | 'historical';

export type EnhancedSuggestion = {
  id: string;
  title: string;
  description: string;
  day?: number;
  activityType?: ActivityType;
  categoryLabel?: string;
  location?: string;
  address?: string;
  estimatedCost?: number;
  currency?: string;
  ticketLink?: string;
  duration?: string;
  bestTime?: string;
  weatherDependent?: boolean;
  needsReservation?: boolean;
  confidence?: number; // 0-1 score
  imageUrl?: string;
  alternatives?: string[];
  budgetBreakdown?: {
    category: string;
    amount: number;
  }[];
};

type EnhancedSuggestionCardProps = {
  suggestion: EnhancedSuggestion;
  onApply?: () => void;
  onSaveMaybe?: () => void;
  onDismiss?: () => void;
  onViewAlternatives?: () => void;
  showImage?: boolean;
  isAdding?: boolean;
  isAdded?: boolean;
  isSavingMaybe?: boolean;
  tripId?: string; // For voting
};

const ACTIVITY_TYPE_CONFIG: Record<ActivityType, { icon: keyof typeof Ionicons.glyphMap; color: string; label: string }> = {
  food: { icon: 'restaurant-outline', color: '#FF6B6B', label: 'Food' },
  culture: { icon: 'library-outline', color: '#4ECDC4', label: 'Culture' },
  adventure: { icon: 'bicycle-outline', color: '#45B7D1', label: 'Adventure' },
  nature: { icon: 'leaf-outline', color: '#96CEB4', label: 'Nature' },
  nightlife: { icon: 'wine-outline', color: '#FFEAA7', label: 'Nightlife' },
  shopping: { icon: 'bag-outline', color: '#DDA15E', label: 'Shopping' },
  relaxation: { icon: 'heart-outline', color: '#A8DADC', label: 'Relaxation' },
  historical: { icon: 'library-outline', color: '#B19CD9', label: 'Historical' },
};

export function EnhancedSuggestionCard({
  suggestion,
  onApply,
  onSaveMaybe,
  onDismiss,
  onViewAlternatives,
  showImage = true,
  isAdding = false,
  isAdded = false,
  isSavingMaybe = false,
  tripId,
}: EnhancedSuggestionCardProps) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  const activityConfig = suggestion.activityType 
    ? ACTIVITY_TYPE_CONFIG[suggestion.activityType]
    : null;
  const categoryLabel = suggestion.categoryLabel;

  // Determine image URL - use provided, or try to fetch from destination
  const { imageUrl, isLoading: resolvingImage } = useLandmarkImage(
    suggestion.location,
    suggestion.imageUrl ?? null
  );

  const confidenceColor = suggestion.confidence 
    ? suggestion.confidence > 0.7 
      ? theme.colors.success 
      : suggestion.confidence > 0.4 
        ? theme.colors.warning 
        : theme.colors.textSecondary
    : undefined;

  // Ensure we always show at least the day badge if day is present
  const hasContent = suggestion.day || suggestion.title || suggestion.description;

  if (!hasContent) {
    return null;
  }

  return (
    <Card 
      variant="outlined" 
      style={{ 
        overflow: 'hidden', 
        marginBottom: theme.spacing(0),
        // Subtle border to make enhanced cards more distinct
        borderWidth: 1.5,
      }}
    >
      {/* Header with Image */}
      {showImage && imageUrl && !imageError && (
        <View style={{ position: 'relative', height: 180 }}>
          <Image
            source={{ uri: imageUrl }}
            style={{ width: '100%', height: '100%' }}
            resizeMode="cover"
            onLoadStart={() => setImageLoading(true)}
            onLoadEnd={() => setImageLoading(false)}
            onError={() => {
              setImageError(true);
              setImageLoading(false);
            }}
          />
          {(resolvingImage || imageLoading) && (
            <View
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: theme.colors.borderMuted,
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <ActivityIndicator color={theme.colors.primary} />
            </View>
          )}

          {/* Overlay badges */}
          <View
            style={{
              position: 'absolute',
              top: theme.spacing(1),
              left: theme.spacing(1),
              right: theme.spacing(1),
              flexDirection: 'row',
              gap: theme.spacing(1),
              flexWrap: 'wrap',
            }}
          >
            {suggestion.day && (
              <View
                style={{
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  paddingHorizontal: theme.spacing(1.5),
                  paddingVertical: theme.spacing(0.5),
                  borderRadius: theme.radii.md,
                }}
              >
                <Caption color="#fff">Day {suggestion.day}</Caption>
              </View>
            )}
            {activityConfig && (
              <View
                style={{
                  backgroundColor: activityConfig.color + 'E6',
                  paddingHorizontal: theme.spacing(1.5),
                  paddingVertical: theme.spacing(0.5),
                  borderRadius: theme.radii.md,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: theme.spacing(0.5),
                }}
              >
                <Ionicons name={activityConfig.icon} size={14} color="#fff" />
                <Caption color="#fff">{activityConfig.label}</Caption>
              </View>
            )}
            {categoryLabel && (
              <View
                style={{
                  backgroundColor: theme.colors.primary + 'E6',
                  paddingHorizontal: theme.spacing(1.5),
                  paddingVertical: theme.spacing(0.5),
                  borderRadius: theme.radii.md,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: theme.spacing(0.5),
                }}
              >
                <Ionicons name="pricetag-outline" size={14} color="#fff" />
                <Caption color="#fff">{categoryLabel}</Caption>
              </View>
            )}
            {suggestion.confidence && (
              <View
                style={{
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  paddingHorizontal: theme.spacing(1.5),
                  paddingVertical: theme.spacing(0.5),
                  borderRadius: theme.radii.md,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: theme.spacing(0.5),
                }}
              >
                <Ionicons 
                  name="star" 
                  size={14} 
                  color={confidenceColor || theme.colors.textSecondary} 
                />
                <Caption color="#fff">
                  {Math.round(suggestion.confidence * 100)}% match
                </Caption>
              </View>
            )}
          </View>
        </View>
      )}

      {/* Content */}
      <View style={{ gap: theme.spacing(1.5), padding: theme.spacing(1.5), paddingTop: theme.spacing(1.25) }}>
        {/* Badges section - always show, even without image */}
        {/* Always show badges section if we have a day, activity type, or any content */}
        <View
          style={{
            flexDirection: 'row',
            gap: theme.spacing(1),
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          {suggestion.day && (
            <View
              style={{
                backgroundColor: theme.colors.primary + '15',
                paddingHorizontal: theme.spacing(1.5),
                paddingVertical: theme.spacing(0.5),
                borderRadius: theme.radii.md,
                borderWidth: 1,
                borderColor: theme.colors.primary + '30',
              }}
            >
              <Caption color={theme.colors.primary} style={{ fontWeight: '600' }}>
                Day {suggestion.day}
              </Caption>
            </View>
          )}
          {activityConfig && (
              <View
                style={{
                  backgroundColor: activityConfig.color + '20',
                  paddingHorizontal: theme.spacing(1.5),
                  paddingVertical: theme.spacing(0.5),
                  borderRadius: theme.radii.md,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: theme.spacing(0.5),
                  borderWidth: 1,
                  borderColor: activityConfig.color + '40',
                }}
              >
                <Ionicons name={activityConfig.icon} size={14} color={activityConfig.color} />
                <Caption color={activityConfig.color} style={{ fontWeight: '600' }}>
                  {activityConfig.label}
                </Caption>
              </View>
            )}
            {categoryLabel && (
              <View
                style={{
                  backgroundColor: theme.colors.primary + '12',
                  paddingHorizontal: theme.spacing(1.5),
                  paddingVertical: theme.spacing(0.5),
                  borderRadius: theme.radii.md,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: theme.spacing(0.5),
                  borderWidth: 1,
                  borderColor: theme.colors.primary + '30',
                }}
              >
                <Ionicons name="pricetag-outline" size={14} color={theme.colors.primary} />
                <Caption color={theme.colors.primary} style={{ fontWeight: '600' }}>
                  {categoryLabel}
                </Caption>
              </View>
            )}
            {suggestion.confidence && (
              <View
                style={{
                  backgroundColor: theme.colors.borderMuted,
                  paddingHorizontal: theme.spacing(1.5),
                  paddingVertical: theme.spacing(0.5),
                  borderRadius: theme.radii.md,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: theme.spacing(0.5),
                }}
              >
                <Ionicons 
                  name="star" 
                  size={14} 
                  color={confidenceColor || theme.colors.textSecondary} 
                />
                <Caption color={theme.colors.textSecondary}>
                  {Math.round(suggestion.confidence * 100)}% match
                </Caption>
              </View>
            )}
        </View>

        {/* Title and location */}
        <View style={{ gap: theme.spacing(0.5) }}>
          <SubHeading>{suggestion.title}</SubHeading>
          {suggestion.address && (
            <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing(0.5) }}>
              <Ionicons name="location-outline" size={16} color={theme.colors.textSecondary} style={{ marginTop: 2 }} />
              <View style={{ flex: 1 }}>
                <Caption color={theme.colors.textSecondary} style={{ fontSize: 13, lineHeight: 18 }}>
                  {suggestion.address}
                </Caption>
              </View>
            </View>
          )}
          {!suggestion.address && suggestion.location && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
              <Ionicons name="location-outline" size={16} color={theme.colors.textSecondary} />
              <Caption color={theme.colors.textSecondary}>{suggestion.location}</Caption>
            </View>
          )}
        </View>

        {/* Description */}
        <Body>{suggestion.description}</Body>

        {/* Key Information Pills */}
        <View
          style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: theme.spacing(1),
          }}
        >
          {suggestion.duration && (
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: theme.spacing(0.5),
                paddingHorizontal: theme.spacing(1.5),
                paddingVertical: theme.spacing(0.75),
                backgroundColor: theme.colors.borderMuted,
                borderRadius: theme.radii.md,
              }}
            >
              <Ionicons name="time-outline" size={14} color={theme.colors.textSecondary} />
              <Caption color={theme.colors.textSecondary}>{suggestion.duration}</Caption>
            </View>
          )}
          {suggestion.bestTime && (
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: theme.spacing(0.5),
                paddingHorizontal: theme.spacing(1.5),
                paddingVertical: theme.spacing(0.75),
                backgroundColor: theme.colors.borderMuted,
                borderRadius: theme.radii.md,
              }}
            >
              <Ionicons name="sunny-outline" size={14} color={theme.colors.textSecondary} />
              <Caption color={theme.colors.textSecondary}>{suggestion.bestTime}</Caption>
            </View>
          )}
          {suggestion.weatherDependent && (
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: theme.spacing(0.5),
                paddingHorizontal: theme.spacing(1.5),
                paddingVertical: theme.spacing(0.75),
                backgroundColor: theme.colors.warning + '20',
                borderRadius: theme.radii.md,
              }}
            >
              <Ionicons name="rainy-outline" size={14} color={theme.colors.warning} />
              <Caption color={theme.colors.warning}>Weather dependent</Caption>
            </View>
          )}
          {suggestion.needsReservation && (
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: theme.spacing(0.5),
                paddingHorizontal: theme.spacing(1.5),
                paddingVertical: theme.spacing(0.75),
                backgroundColor: theme.colors.error + '20',
                borderRadius: theme.radii.md,
              }}
            >
              <Ionicons name="calendar-outline" size={14} color={theme.colors.error} />
              <Caption color={theme.colors.error}>Reservation needed</Caption>
            </View>
          )}
        </View>

        {/* Cost and Ticket Link */}
        {(suggestion.estimatedCost || suggestion.ticketLink) && (
          <View style={{ gap: theme.spacing(1) }}>
            {suggestion.estimatedCost && (
              <View
                style={{
                  padding: theme.spacing(1.5),
                  backgroundColor: theme.colors.borderMuted,
                  borderRadius: theme.radii.md,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: theme.spacing(1),
                }}
              >
                <Ionicons name="cash-outline" size={18} color={theme.colors.primary} />
                <ThemedText variant="bodyStrong">
                  {suggestion.currency || 'USD'} {suggestion.estimatedCost.toLocaleString()}
                </ThemedText>
                <Caption color={theme.colors.textSecondary} style={{ marginLeft: 'auto' }}>
                  per person
                </Caption>
              </View>
            )}
            {suggestion.ticketLink && (
              <Pressable
                onPress={() => {
                  // Open ticket link in browser
                  if (Platform.OS === 'web' && typeof window !== 'undefined' && window.open) {
                    window.open(suggestion.ticketLink, '_blank');
                  } else {
                    // For React Native, use Linking
                    Linking.openURL(suggestion.ticketLink || '').catch((err: Error) => {
                      console.error('Failed to open ticket link:', err);
                    });
                  }
                }}
                style={({ pressed }) => ({
                  padding: theme.spacing(1.5),
                  backgroundColor: theme.colors.primary + '15',
                  borderRadius: theme.radii.md,
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: theme.spacing(1),
                  borderWidth: 1,
                  borderColor: theme.colors.primary + '30',
                  opacity: pressed ? 0.8 : 1,
                })}
              >
                <Ionicons name="ticket-outline" size={18} color={theme.colors.primary} />
                <ThemedText variant="bodyStrong" style={{ color: theme.colors.primary }}>
                  Buy Tickets
                </ThemedText>
                <Ionicons name="open-outline" size={16} color={theme.colors.primary} />
              </Pressable>
            )}
          </View>
        )}

        {/* Expanded Budget Breakdown */}
        {expanded && suggestion.budgetBreakdown && suggestion.budgetBreakdown.length > 0 && (
          <View
            style={{
              padding: theme.spacing(1.5),
              backgroundColor: theme.colors.background,
              borderRadius: theme.radii.md,
              gap: theme.spacing(1),
            }}
          >
            <Caption color={theme.colors.textSecondary} style={{ fontWeight: '600' }}>
              Cost Breakdown:
            </Caption>
            {suggestion.budgetBreakdown.map((item, index) => (
              <View
                key={index}
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Caption color={theme.colors.textSecondary}>{item.category}</Caption>
                <ThemedText variant="bodyStrong">
                  ${item.amount.toLocaleString()}
                </ThemedText>
              </View>
            ))}
          </View>
        )}

        {/* Alternatives hint */}
        {suggestion.alternatives && suggestion.alternatives.length > 0 && (
          <View
            style={{
              padding: theme.spacing(1.5),
              backgroundColor: theme.colors.primary + '10',
              borderRadius: theme.radii.md,
              flexDirection: 'row',
              alignItems: 'center',
              gap: theme.spacing(1),
            }}
          >
            <Ionicons name="bulb-outline" size={18} color={theme.colors.primary} />
            <View style={{ flex: 1 }}>
              <Caption color={theme.colors.primary} style={{ fontWeight: '600' }}>
                {suggestion.alternatives.length} alternative{suggestion.alternatives.length !== 1 ? 's' : ''} available
              </Caption>
            </View>
            {onViewAlternatives && (
              <Pressable onPress={onViewAlternatives}>
                <Caption color={theme.colors.primary} style={{ fontWeight: '600' }}>
                  View →
                </Caption>
              </Pressable>
            )}
          </View>
        )}

        {/* Voting Section */}
        {tripId && (
          <View style={{ 
            flexDirection: 'row', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            paddingTop: theme.spacing(1),
            borderTopWidth: 1,
            borderTopColor: theme.colors.borderMuted,
          }}>
            <View style={{ flex: 1 }}>
              <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                Group vote on this suggestion
              </Caption>
            </View>
            <VoteButton
              tripId={tripId}
              itemId={suggestion.id || `${suggestion.day || 'day'}-${suggestion.title}`}
              itemType="suggestion"
              size="small"
              showCounts={true}
              orientation="horizontal"
            />
          </View>
        )}

        {/* Action Buttons */}
        <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
          {(onApply || isAdded) && (
            <Button
              label={isAdded ? 'Added ✓' : isAdding ? "Adding..." : "Add to itinerary"}
              onPress={isAdded ? () => {} : onApply}
              disabled={isAdding || isAdded || !onApply}
              icon={
                isAdded ? (
                  <Ionicons name="checkmark-circle-outline" size={20} color={theme.colors.surface} />
                ) : isAdding ? (
                  <ActivityIndicator size="small" color={theme.colors.surface} />
                ) : (
                  <Ionicons name="add-circle-outline" size={20} color={theme.colors.surface} />
                )
              }
              style={{ flex: 1, opacity: isAdding || isAdded ? 0.6 : 1 }}
            />
          )}
          {onSaveMaybe && (
            <Button
              label={isSavingMaybe ? 'Saving...' : 'Save to Maybe'}
              variant="secondary"
              onPress={onSaveMaybe}
              disabled={isSavingMaybe}
              icon={<Ionicons name="bookmark-outline" size={18} color={theme.colors.primary} />}
              style={{ flex: 1, opacity: isSavingMaybe ? 0.7 : 1 }}
            />
          )}
          {onDismiss && (
            <Pressable
              onPress={onDismiss}
              style={({ pressed }) => ({
                padding: theme.spacing(1.75),
                borderRadius: theme.radii.lg,
                borderWidth: 1,
                borderColor: theme.colors.border,
                backgroundColor: theme.colors.surface,
                justifyContent: 'center',
                alignItems: 'center',
                opacity: pressed ? 0.7 : 1,
              })}
            >
              <Ionicons name="close-outline" size={20} color={theme.colors.textSecondary} />
            </Pressable>
          )}
        </View>
      </View>
    </Card>
  );
}
